import type { ReactNode } from 'react';
import { ErrorState } from '../../../../components/ErrorState';
import { Modal } from '../../../../components/Modal';
import { Skeleton } from '../../../../components/Skeleton';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { useAuditLogEntry } from '../useAuditLogEntry';
import { EntityChangeList } from './EntityChangeList';

/**
 * One audit entry in full — frontend reference §13.
 *
 * This modal is the ONE place a detail view keeps uppercase labels and pill/tint chrome.
 * Everywhere else uses the plain-case `<PreviewTable>`; here the field-level change table
 * is the mandated shape, and the tints carry the insert/update/delete distinction.
 *
 * It remains the depth for the per-module History Logs modal, which cannot navigate away
 * without discarding the list the user opened it from. The global Audit Logs page opens
 * the profile page instead (`/audit-logs/:id`) — same entry, plus the captured context
 * that does not fit in a modal.
 */
export function AuditLogDetailModal({
  id,
  onClose,
}: {
  id: number | null;
  onClose: () => void;
}) {
  const { entry, changes, loading, error } = useAuditLogEntry(id);

  if (id === null) {
    return null;
  }

  return (
    <Modal open onClose={onClose} title="Audit Log Entry" size="xl" id="audit-detail-modal">
      {loading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-5 w-full" />
          ))}
        </div>
      ) : error ? (
        <ErrorState message={error.message} correlationId={error.correlationId} />
      ) : entry ? (
        <div className="flex flex-col gap-5">
          {/* 1. Request context */}
          <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            <Field label="Timestamp">
              <time dateTime={toIso(entry.timestamp)} className="font-mono tabular-nums">
                {formatDateTime(entry.timestamp)}
              </time>
            </Field>

            <Field label="Action">
              <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700 ring-1 ring-slate-200">
                {entry.action}
              </span>
            </Field>

            <Field label="Actioner">{entry.userName ?? 'anonymous'}</Field>
            <Field label="Email" mono>
              {entry.userEmail}
            </Field>
            <Field label="User ID" mono>
              {entry.userId}
            </Field>
            <Field label="IP Address" mono>
              {entry.ipAddress}
            </Field>

            <Field label="Request" mono>
              {entry.httpMethod ? `${entry.httpMethod} ${entry.path ?? ''}` : null}
            </Field>

            <Field label="Status">
              {entry.statusCode ? (
                <span className="inline-flex items-center gap-1.5">
                  <span className="font-mono tabular-nums">{entry.statusCode}</span>
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[11px] ring-1 ${
                      entry.succeeded
                        ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                        : 'bg-red-50 text-red-700 ring-red-200'
                    }`}
                  >
                    {entry.succeeded ? 'OK' : 'Failed'}
                  </span>
                </span>
              ) : null}
            </Field>

            <Field label="Duration">
              {entry.durationMs != null ? (
                <span className="font-mono tabular-nums">{entry.durationMs} ms</span>
              ) : null}
            </Field>

            <Field label="Entity">
              {entry.entityType
                ? [entry.entityType, entry.entityDisplay, entry.entityId ? `(${entry.entityId})` : null]
                    .filter(Boolean)
                    .join(' · ')
                : null}
            </Field>

            {/* Only present when there is something to say. */}
            {entry.userAgent ? (
              <div className="sm:col-span-2">
                <Field label="User Agent" mono>
                  {entry.userAgent}
                </Field>
              </div>
            ) : null}

            {entry.errorMessage ? (
              <div className="sm:col-span-2">
                <Field label="Error">
                  <span className="text-red-700">{entry.errorMessage}</span>
                </Field>
              </div>
            ) : null}
          </div>

          {/* 2. Entity changes */}
          <section>
            <h3 className="pb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              Entity Changes
            </h3>

            <EntityChangeList changes={changes} />
          </section>
        </div>
      ) : null}
    </Modal>
  );
}

function Field({
  label,
  children,
  mono = false,
}: {
  label: string;
  children: ReactNode;
  mono?: boolean;
}) {
  const empty = children === null || children === undefined || children === '';

  return (
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      <p className={`min-w-0 break-words text-sm text-slate-700 ${mono ? 'font-mono tabular-nums' : ''}`}>
        {empty ? <span className="text-slate-400">—</span> : children}
      </p>
    </div>
  );
}
